import { ActionReducerMap, MetaReducer } from '@ngrx/store';
import { routerReducer, RouterReducerState } from '@ngrx/router-store';
import { environment } from '../../environments/environment';

/**
 * Root state interface for the entire application
 * 
 * This defines the shape of the global application state.
 * Feature states are registered separately via StoreModule.forFeature()
 * but TypeScript types for them can be added here for type safety.
 */
export interface AppState {
  router: RouterReducerState;
  // Feature states are added via forFeature() but listed here for reference:
  // auth: AuthState; (imported as feature)
  // user: UserState; (imported as feature)
  // feedback: FeedbackState; (imported as feature)
  // userContact: UserContactState; (imported as feature)
  // stripe: StripeState; (imported as feature)
  // avatar: AvatarState; (imported as feature)
  // videoSearch: VideoSearchState; (imported as feature)
  // language: LanguageState; (imported as feature)
}

/**
 * Root reducer map
 * Only root-level reducers are registered here.
 * Feature reducers (like auth) are registered in their respective feature modules.
 */
export const reducers: ActionReducerMap<AppState> = {
  router: routerReducer,
};

/**
 * Meta-reducers for global state handling
 * Meta-reducers intercept actions before they reach reducers.
 * 
 * Common use cases:
 * - Logging (development only)
 * - State reset on logout
 * - State persistence
 * - Performance monitoring
 */
export const metaReducers: MetaReducer<AppState>[] = !environment.production 
  ? [] // Add [logger] here to enable action logging in development
  : [];

/**
 * Console logger meta-reducer for actions and state changes (development only)
 * Uncomment in metaReducers array above to enable
 */
export function logger(reducer: any) {
  return function(state: any, action: any) {
    const result = reducer(state, action);
    console.groupCollapsed(action.type);
    console.log('prev state', state);
    console.log('action', action);
    console.log('next state', result);
    console.groupEnd();
    return result;
  };
}

/**
 * Export store module for easy imports
 */
export { StateModule } from './state.module';

/**
 * Export feature modules
 */
export { AuthStateModule } from './auth/auth-state.module';
export { FeedbackStateModule } from './feedback/feedback-state.module';
export { ManagerFeedbackStateModule } from './manager-feedback/manager-feedback-state.module';

/**
 * Export feature facades (recommended way for components to interact with state)
 */
export { AuthFacade } from './auth/auth.facade';
export { FeedbackFacade } from './feedback/feedback.facade';
export { VideoSearchFacade } from './video-search/video-search.facade';
export { UserPreferencesFacade } from './user-preferences/user-preferences.facade';
export { UserSettingsFacade } from './user-settings/user-settings.facade';
export { ManagerFeedbackFacade } from './manager-feedback/manager-feedback.facade';

/**
 * Matching feature exports
 */
export { MatchingStateModule } from './matching/matching-state.module';
export { MatchingFacade } from './matching/matching.facade';
export { CareLinksStateModule } from './care-links/care-links-state.module';
export { CareLinksFacade } from './care-links/care-links.facade';
